// Exporting projects from this browser (W8): each project's documents, brought up to date from the relay when online
// (else as this computer has them, with any edits still waiting to be sent), built into a `.writingsuite` package
// (store/packages.ts) and zipped. Only the browser can do this: the relay holds nothing it could read.
import type * as A from '@automerge/automerge';
import { LiveProject, type ProjectDeps } from './project.ts';
import { buildPackage, safeFilename, zipPackages, type Package } from './packages.ts';
import { projectTitle, type LibraryProject } from './library.ts';
import { saveFile } from '../host/host.ts';

export interface ExportProgress {
  /** 'reading': a project's documents are being gathered; 'packing': its package built; 'zipping': files going in. */
  phase: 'reading' | 'packing' | 'zipping' | 'done';
  /** The project being worked on (1-based) and how many in all. */
  index: number;
  total: number;
  title: string;
  /** 0–1 overall. */
  fraction: number;
}

export interface ExportResult {
  /** The zip's suggested name. */
  name: string;
  zip: Uint8Array;
  packages: Package[];
  /** Projects that couldn't be exported, and why (the rest are in the zip). */
  failed: { id: string; title: string; reason: string }[];
}

/** One project's documents as they are now (`LiveProject` without its socket afterwards). */
export type ReadDocs = (project: LibraryProject) => Promise<Map<string, A.Doc<unknown>>>;

export function liveReader(deps: ProjectDeps): ReadDocs {
  return async (p) => {
    const live = new LiveProject(p.id, p.key, deps, false);
    try {
      await live.open();
      return live.documents();
    } finally {
      live.close();
    }
  };
}

const yieldToPage = () => new Promise<void>((r) => setTimeout(r, 0));

/** The zip's name: one project → "<Title>.writingsuite.zip"; several → "Five Rooms export <yyyy-mm-dd>.zip". */
export function exportName(projects: LibraryProject[], now = new Date()): string {
  if (projects.length === 1) return `${safeFilename(projectTitle(projects[0]))}.writingsuite.zip`;
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return `Five Rooms export ${day}.zip`;
}

/** Packages every project given (in order), then zips them, reporting progress along the way. */
export async function exportProjects(
  projects: LibraryProject[],
  read: ReadDocs,
  onProgress: (p: ExportProgress) => void = () => {},
  o: { now?: Date; signal?: AbortSignal } = {},
): Promise<ExportResult> {
  const total = projects.length;
  const packages: Package[] = [];
  const failed: ExportResult['failed'] = [];
  const now = o.now ?? new Date();
  for (let i = 0; i < total; i++) {
    if (o.signal?.aborted) throw new DOMException('Export stopped', 'AbortError');
    const p = projects[i];
    const title = projectTitle(p);
    onProgress({ phase: 'reading', index: i + 1, total, title, fraction: (i / total) * 0.8 });
    await yieldToPage();
    try {
      const docs = await read(p);
      onProgress({ phase: 'packing', index: i + 1, total, title, fraction: ((i + 0.5) / total) * 0.8 });
      await yieldToPage();
      packages.push(await buildPackage(docs, { exportedAt: now }));
    } catch (e) {
      failed.push({ id: p.id, title, reason: e instanceof Error ? e.message : String(e) });
    }
  }
  if (!packages.length) {
    throw new Error(failed.length ? `Nothing could be exported: ${failed.map((f) => `“${f.title}” (${f.reason})`).join('; ')}` : 'There are no projects to export.');
  }
  onProgress({ phase: 'zipping', index: total, total, title: '', fraction: 0.85 });
  await yieldToPage();
  const zip = zipPackages(packages, (done, all) => onProgress({ phase: 'zipping', index: total, total, title: '', fraction: 0.85 + 0.15 * (done / all) }));
  onProgress({ phase: 'done', index: total, total, title: '', fraction: 1 });
  return { name: exportName(projects, now), zip, packages, failed };
}

/** Saves bytes as a file: the shell's Save dialog, else the browser's download (host/host.ts). */
export function saveDownload(name: string, bytes: Uint8Array, mime = 'application/zip') {
  void saveFile(name, bytes, mime);
}
